import * as Sentry from "@sentry/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { errorViewedEvent } from "~/tooling/telemetry/events";

export function resolveErrorMessage(
	customErrorMessage: string | undefined,
	isServiceUnavailable: boolean,
	serviceUnavailableMessage: string,
	fallbackMessage: string,
): string {
	return (
		customErrorMessage ??
		(isServiceUnavailable ? serviceUnavailableMessage : fallbackMessage)
	);
}

/**
 * Fires an `errorViewedEvent` once per captured Sentry error in the
 * root ErrorBoundary. Extracted from root.tsx so the logic is unit-testable.
 */
export function useErrorBoundaryTracking({
	errorId,
	isNetworkFailure,
	isStaleBundle,
	isServiceUnavailable,
	customErrorMessage,
}: {
	errorId: string | undefined;
	isNetworkFailure: boolean;
	isStaleBundle: boolean;
	isServiceUnavailable: boolean;
	customErrorMessage: string | undefined;
}) {
	const { t } = useTranslation();

	// biome-ignore lint/correctness/useExhaustiveDependencies: fire once per Sentry error capture
	useEffect(() => {
		if (!errorId || isNetworkFailure || isStaleBundle) return;

		const message = resolveErrorMessage(
			customErrorMessage,
			isServiceUnavailable,
			t("errors.serviceUnavailable.message"),
			t("errors.somethingWentWrong"),
		);

		const traceId = Sentry.getActiveSpan()?.spanContext().traceId;
		captureEvent(errorViewedEvent(traceId, message));
	}, [errorId]);
}
